(() => {
  "use strict";

  const VERSION = "1.1.0";
  const ROOT_SELECTOR = "[data-trnd-portalkey-player]";
  const STORAGE_PREFIX = "trndPortalKey:v1:";
  const FRIENDLY_MEDIA_ERROR =
    "This track could not be played. Please check the audio file URL or try another track.";
  const MEDIA_ACTIONS = [
    "play",
    "pause",
    "previoustrack",
    "nexttrack",
    "seekbackward",
    "seekforward",
    "seekto",
    "stop"
  ];
  const VALID_REPEAT_MODES = new Set(["off", "all", "one"]);
  const VALID_MIME_TYPES = new Set([
    "audio/mpeg",
    "audio/aac",
    "audio/mp4",
    "application/vnd.apple.mpegurl",
    "audio/x-mpegurl"
  ]);
  const HLS_MIME_TYPES = new Set([
    "application/vnd.apple.mpegurl",
    "audio/x-mpegurl"
  ]);
  const BLOCKED_AUDIO_HOSTS = [
    /(^|\.)youtube\.com$/i,
    /(^|\.)youtube-nocookie\.com$/i,
    /(^|\.)youtu\.be$/i,
    /(^|\.)spotify\.com$/i,
    /(^|\.)soundcloud\.com$/i
  ];

  const existingApi = window.TRNDPortalKeyPlayer;
  if (
    existingApi &&
    existingApi.version === VERSION &&
    !existingApi.destroyed &&
    typeof existingApi.scan === "function"
  ) {
    existingApi.scan(document);
    return;
  }

  const inheritedAudio = existingApi?.engine?.audio || null;
  if (existingApi && typeof existingApi.destroy === "function") {
    existingApi.destroy({ keepAudio: true });
  }

  class PortalKeyView {
    constructor(root, config, engine) {
      this.root = root;
      this.config = config;
      this.engine = engine;
      this.sectionId = config.sectionId;
      this.abortController = new AbortController();
      this.isScrubbing = false;
      this.lastStatusText = null;
      this.lastErrorText = null;
      this.miniAnchor = null;
      this.isMiniPortaled = false;
      this.nodes = this.collectNodes();

      this.portalMiniPlayer();
      this.bindEvents();
    }

    collectNodes() {
      const root = this.root;

      return {
        card: root.querySelector("[data-trnd-card]"),
        title: root.querySelector("[data-trnd-title]"),
        artist: root.querySelector("[data-trnd-artist]"),
        album: root.querySelector("[data-trnd-album]"),
        description: root.querySelector("[data-trnd-description]"),
        explicit: root.querySelector("[data-trnd-explicit]"),
        artwork: root.querySelector("[data-trnd-artwork]"),
        artworkPlaceholder: root.querySelector("[data-trnd-artwork-placeholder]"),
        playingLabel: root.querySelector("[data-trnd-playing-label]"),
        liveBadge: root.querySelector("[data-trnd-live-badge]"),
        liveText: root.querySelector("[data-trnd-live-text]"),
        currentTime: root.querySelector("[data-trnd-current-time]"),
        duration: root.querySelector("[data-trnd-duration]"),
        progress: root.querySelector("[data-trnd-progress]"),
        volume: root.querySelector("[data-trnd-volume]"),
        status: root.querySelector("[data-trnd-status]"),
        error: root.querySelector("[data-trnd-error]"),
        playlist: root.querySelector("[data-trnd-playlist]"),
        playlistButtons: Array.from(
          root.querySelectorAll('[data-trnd-action="playlist"]')
        ),
        playButtons: Array.from(root.querySelectorAll('[data-trnd-action="play"]')),
        previousButtons: Array.from(
          root.querySelectorAll('[data-trnd-action="previous"]')
        ),
        nextButtons: Array.from(root.querySelectorAll('[data-trnd-action="next"]')),
        shuffleButtons: Array.from(
          root.querySelectorAll('[data-trnd-action="shuffle"]')
        ),
        repeatButtons: Array.from(
          root.querySelectorAll('[data-trnd-action="repeat"]')
        ),
        muteButtons: Array.from(root.querySelectorAll('[data-trnd-action="mute"]')),
        trackRows: Array.from(root.querySelectorAll("[data-trnd-track-row]")),
        trackActions: root.querySelector("[data-trnd-track-actions]"),
        download: root.querySelector("[data-trnd-download]"),
        external: root.querySelector("[data-trnd-external]"),
        miniPosition: root.querySelector("[data-trnd-mini-position]"),
        miniArtwork: root.querySelector("[data-trnd-mini-artwork]"),
        miniArtworkPlaceholder: root.querySelector("[data-trnd-mini-placeholder]"),
        miniTitle: root.querySelector("[data-trnd-mini-title]"),
        miniArtist: root.querySelector("[data-trnd-mini-artist]")
      };
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.root.addEventListener(
        "click",
        (event) => {
          const eventTarget = event.target;
          if (!(eventTarget instanceof Element)) return;

          const actionButton = eventTarget.closest("[data-trnd-action]");
          if (actionButton && this.root.contains(actionButton)) {
            this.handleAction(actionButton.dataset.trndAction);
            return;
          }

          const trackButton = eventTarget.closest("[data-trnd-track-select]");
          if (!trackButton || !this.root.contains(trackButton)) return;

          const index = Number.parseInt(trackButton.dataset.trackIndex, 10);
          if (!Number.isInteger(index)) return;

          this.engine.selectTrack(index, {
            playbackIntent: "paused",
            userSelected: true,
            origin: "playlist"
          });
        },
        { signal }
      );

      if (this.nodes.progress) {
        this.nodes.progress.addEventListener(
          "input",
          () => {
            if (this.nodes.progress.disabled) return;
            this.isScrubbing = true;
            const value = Number(this.nodes.progress.value);
            this.renderSeekPreview(value);
          },
          { signal }
        );

        this.nodes.progress.addEventListener(
          "change",
          () => {
            if (this.nodes.progress.disabled) return;
            const value = Number(this.nodes.progress.value);
            this.isScrubbing = false;
            this.engine.seekTo(value, { origin: "progress" });
          },
          { signal }
        );

        this.nodes.progress.addEventListener(
          "blur",
          () => {
            this.isScrubbing = false;
          },
          { signal }
        );
      }

      if (this.nodes.volume) {
        this.nodes.volume.addEventListener(
          "input",
          () => {
            this.engine.setVolume(Number(this.nodes.volume.value));
          },
          { signal }
        );
      }

      this.root.addEventListener(
        "keydown",
        (event) => this.handleKeyboard(event),
        { signal }
      );

      if (this.nodes.miniPosition && this.isMiniPortaled) {
        this.nodes.miniPosition.addEventListener(
          "click",
          (event) => {
            const eventTarget = event.target;
            if (!(eventTarget instanceof Element)) return;

            const actionButton = eventTarget.closest("[data-trnd-action]");
            if (
              !actionButton ||
              !this.nodes.miniPosition.contains(actionButton)
            ) {
              return;
            }

            this.handleAction(actionButton.dataset.trndAction);
          },
          { signal }
        );

        this.nodes.miniPosition.addEventListener(
          "keydown",
          (event) => this.handleKeyboard(event),
          { signal }
        );
      }

      if (this.nodes.artwork) {
        this.nodes.artwork.addEventListener(
          "error",
          () => this.showArtworkFallback(false),
          { signal }
        );
      }

      if (this.nodes.miniArtwork) {
        this.nodes.miniArtwork.addEventListener(
          "error",
          () => this.showArtworkFallback(true),
          { signal }
        );
      }
    }

    handleAction(action) {
      switch (action) {
        case "play":
          this.engine.togglePlay({ origin: "play-button" });
          break;
        case "previous":
          this.engine.moveTrack(-1, { origin: "previous-button" });
          break;
        case "next":
          this.engine.moveTrack(1, { origin: "next-button" });
          break;
        case "shuffle":
          this.engine.toggleShuffle();
          break;
        case "repeat":
          this.engine.cycleRepeat();
          break;
        case "mute":
          this.engine.toggleMute();
          break;
        case "playlist":
          this.engine.togglePlaylist();
          break;
        case "open-full":
          this.openFullPlayer();
          break;
        default:
          break;
      }
    }

    portalMiniPlayer() {
      const miniPosition = this.nodes.miniPosition;
      const parent = miniPosition?.parentNode;
      if (!miniPosition || !parent || !document.body) return;

      for (const staleMini of document.querySelectorAll(
        "[data-trnd-mini-owner]"
      )) {
        if (
          staleMini !== miniPosition &&
          staleMini.dataset.trndMiniOwner === this.sectionId
        ) {
          staleMini.remove();
        }
      }

      this.miniAnchor = document.createComment(
        `TRND PortalKey mini-player ${this.sectionId}`
      );
      parent.insertBefore(this.miniAnchor, miniPosition);
      document.body.appendChild(miniPosition);
      this.isMiniPortaled = true;
    }

    handleKeyboard(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          "input, textarea, select, [contenteditable='true'], [contenteditable='']"
        )
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      const nativeActivator =
        target instanceof Element && target.closest("button, a");

      if (
        (key === " " || key === "spacebar") &&
        !event.repeat &&
        !nativeActivator
      ) {
        event.preventDefault();
        this.engine.togglePlay({ origin: "keyboard" });
        return;
      }

      if (key === "arrowleft") {
        event.preventDefault();
        this.engine.seekBy(-5, { origin: "keyboard" });
        return;
      }

      if (key === "arrowright") {
        event.preventDefault();
        this.engine.seekBy(5, { origin: "keyboard" });
        return;
      }

      if (key === "arrowup") {
        event.preventDefault();
        this.engine.adjustVolume(0.05);
        return;
      }

      if (key === "arrowdown") {
        event.preventDefault();
        this.engine.adjustVolume(-0.05);
        return;
      }

      if (event.repeat) return;

      if (key === "m") {
        event.preventDefault();
        this.engine.toggleMute();
      } else if (key === "n") {
        event.preventDefault();
        this.engine.moveTrack(1, { origin: "keyboard" });
      } else if (key === "p") {
        event.preventDefault();
        this.engine.moveTrack(-1, { origin: "keyboard" });
      }
    }

    renderSeekPreview(seconds) {
      const duration = this.engine.getFiniteDuration();
      if (!Number.isFinite(seconds) || !Number.isFinite(duration)) return;

      const clamped = clamp(seconds, 0, duration);
      const percent = duration > 0 ? (clamped / duration) * 100 : 0;

      if (this.nodes.currentTime) {
        this.nodes.currentTime.textContent = formatClockTime(clamped);
      }

      this.nodes.progress.style.setProperty(
        "--trnd-range-progress",
        `${clamp(percent, 0, 100)}%`
      );
      this.nodes.progress.setAttribute(
        "aria-valuetext",
        `${formatSpokenTime(clamped)} of ${formatSpokenTime(duration)}`
      );
    }

    openFullPlayer() {
      const reducedMotion = window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      this.root.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "center"
      });

      const primaryPlayButton = this.nodes.playButtons.find(
        (button) => !button.closest("[data-trnd-mini]")
      );

      window.setTimeout(() => primaryPlayButton?.focus(), reducedMotion ? 0 : 350);
    }

    openPlaylistFromEditor(blockId) {
      this.engine.setPlaylistOpen(true, { persist: false, announce: false });

      const row =
        this.nodes.trackRows.find(
          (candidate) => candidate.dataset.trackId === String(blockId)
        ) ||
        Array.from(
          this.root.querySelectorAll("[data-trnd-editor-track-anchor]")
        ).find((candidate) => candidate.dataset.trackId === String(blockId));

      if (row && !row.matches("[data-trnd-editor-track-anchor]")) {
        row.scrollIntoView({ block: "nearest", behavior: "auto" });
      } else {
        this.root.scrollIntoView({ block: "nearest", behavior: "auto" });
      }
    }

    render(snapshot) {
      const track = snapshot.track;
      const hasTrack = Boolean(track);
      const isPlaying = snapshot.isPlaying;
      const title = track?.title || "Select a track";
      const artist = track?.artist || "TRND PortalKey";

      setText(this.nodes.title, title);
      setText(this.nodes.artist, artist);
      setText(this.nodes.album, track?.album || "");
      setHidden(this.nodes.album, !track?.album);
      setText(this.nodes.description, track?.description || "");
      setHidden(this.nodes.description, !track?.description);
      setHidden(this.nodes.explicit, !track?.explicit);

      this.updateArtwork(track);

      if (this.nodes.playingLabel) {
        let label = "Now selected";
        if (snapshot.loading) label = "Loading";
        else if (isPlaying) label = "Now playing";
        else if (snapshot.hasUserStartedPlayback) label = "Paused";
        this.nodes.playingLabel.textContent = label;
      }

      setHidden(this.nodes.liveBadge, !snapshot.isLive);
      setHidden(this.nodes.liveText, !snapshot.isLive);

      if (this.nodes.currentTime && !this.isScrubbing) {
        this.nodes.currentTime.textContent = formatClockTime(snapshot.currentTime);
      }

      if (this.nodes.duration) {
        this.nodes.duration.textContent = snapshot.isLive
          ? "--:--"
          : snapshot.hasFiniteDuration
            ? formatClockTime(snapshot.duration)
            : "--:--";
      }

      this.renderProgress(snapshot);
      this.renderVolume(snapshot);
      this.renderPlayButtons(snapshot, title);
      this.renderModeButtons(snapshot);
      this.renderPlaylist(snapshot);
      this.renderTrackRows(snapshot);
      this.renderTrackActions(snapshot);
      this.renderMiniPlayer(snapshot, title, artist);

      for (const button of [
        ...this.nodes.previousButtons,
        ...this.nodes.nextButtons
      ]) {
        button.disabled = !hasTrack;
      }

      for (const button of this.nodes.shuffleButtons) {
        button.disabled = snapshot.trackCount < 2;
      }

      for (const button of this.nodes.repeatButtons) {
        button.disabled = !hasTrack;
      }

      this.setStatus(snapshot.status);
      this.setError(snapshot.error);
    }

    renderProgress(snapshot) {
      const progress = this.nodes.progress;
      if (!progress) return;

      const enabled = snapshot.canSeek;
      progress.disabled = !enabled;
      progress.setAttribute("aria-disabled", String(!enabled));

      if (!enabled) {
        progress.min = "0";
        progress.max = "100";
        progress.value = "0";
        progress.style.setProperty("--trnd-range-progress", "0%");
        progress.setAttribute(
          "aria-valuetext",
          snapshot.isLive ? "Live stream; seeking unavailable" : "Seeking unavailable"
        );
        return;
      }

      const duration = snapshot.duration;
      const current = clamp(snapshot.currentTime, 0, duration);
      const percent = duration > 0 ? (current / duration) * 100 : 0;

      progress.min = "0";
      progress.max = String(duration);

      if (!this.isScrubbing) {
        progress.value = String(current);
        progress.style.setProperty(
          "--trnd-range-progress",
          `${clamp(percent, 0, 100)}%`
        );
        progress.setAttribute(
          "aria-valuetext",
          `${formatSpokenTime(current)} of ${formatSpokenTime(duration)}`
        );
      }
    }

    renderVolume(snapshot) {
      const volume = this.nodes.volume;
      if (!volume) return;

      const percent = Math.round(snapshot.volume * 100);
      volume.value = String(snapshot.volume);
      volume.style.setProperty("--trnd-range-progress", `${percent}%`);
      volume.setAttribute(
        "aria-valuetext",
        snapshot.muted ? `${percent} percent, muted` : `${percent} percent`
      );
    }

    renderPlayButtons(snapshot, title) {
      for (const button of this.nodes.playButtons) {
        const playIcon = button.querySelector("[data-trnd-icon-play]");
        const pauseIcon = button.querySelector("[data-trnd-icon-pause]");
        const spinner = button.querySelector("[data-trnd-spinner]");

        setHidden(spinner, !snapshot.loading);
        setHidden(playIcon, snapshot.loading || snapshot.isPlaying);
        setHidden(pauseIcon, snapshot.loading || !snapshot.isPlaying);

        button.disabled = !snapshot.track;
        button.setAttribute("aria-busy", String(snapshot.loading));
        button.setAttribute("aria-pressed", String(snapshot.isPlaying));
        button.setAttribute(
          "aria-label",
          `${snapshot.isPlaying ? "Pause" : "Play"} ${title}`
        );
      }
    }

    renderModeButtons(snapshot) {
      for (const button of this.nodes.shuffleButtons) {
        button.setAttribute("aria-pressed", String(snapshot.shuffle));
        button.setAttribute(
          "aria-label",
          `Shuffle ${snapshot.shuffle ? "on" : "off"}`
        );
      }

      for (const button of this.nodes.repeatButtons) {
        button.dataset.repeatMode = snapshot.repeat;
        button.setAttribute("aria-pressed", String(snapshot.repeat !== "off"));
        button.setAttribute("aria-label", `Repeat ${snapshot.repeat}`);
        const one = button.querySelector("[data-trnd-repeat-one]");
        setHidden(one, snapshot.repeat !== "one");
      }

      for (const button of this.nodes.muteButtons) {
        button.setAttribute("aria-pressed", String(snapshot.muted));
        button.setAttribute("aria-label", snapshot.muted ? "Unmute" : "Mute");
        setHidden(button.querySelector("[data-trnd-icon-volume]"), snapshot.muted);
        setHidden(button.querySelector("[data-trnd-icon-muted]"), !snapshot.muted);
      }
    }

    renderPlaylist(snapshot) {
      if (!this.nodes.playlist) return;

      this.nodes.playlist.hidden = !snapshot.playlistOpen;
      for (const button of this.nodes.playlistButtons) {
        button.setAttribute("aria-expanded", String(snapshot.playlistOpen));
        button.setAttribute(
          "aria-label",
          `${snapshot.playlistOpen ? "Close" : "Open"} playlist`
        );
      }
    }

    renderTrackRows(snapshot) {
      for (const row of this.nodes.trackRows) {
        const index = Number.parseInt(row.dataset.trackIndex, 10);
        const isActive = index === snapshot.index;
        const selectButton = row.querySelector("[data-trnd-track-select]");
        const selectLabel = row.querySelector(".trnd-row-select-label");
        const track = this.config.tracks[index];

        row.dataset.active = String(isActive);
        row.dataset.playing = String(isActive && snapshot.isPlaying);

        if (selectButton) {
          if (isActive) selectButton.setAttribute("aria-current", "true");
          else selectButton.removeAttribute("aria-current");

          if (track) {
            const state = isActive ? "Currently selected" : "Select";
            const explicit = track.explicit ? ", explicit content" : "";
            selectButton.setAttribute(
              "aria-label",
              `${state} ${track.title} by ${track.artist}${explicit}`
            );
          }
        }

        if (selectLabel) {
          selectLabel.textContent = isActive ? "Selected" : "Select";
        }
      }
    }

    renderTrackActions(snapshot) {
      const track = snapshot.track;
      let hasVisibleAction = false;

      if (this.nodes.download) {
        const canDownload = Boolean(track?.download && snapshot.validatedSource);
        this.nodes.download.hidden = !canDownload;

        if (canDownload) {
          this.nodes.download.href = snapshot.validatedSource;
          this.nodes.download.setAttribute(
            "aria-label",
            `Download ${track.title}`
          );
          hasVisibleAction = true;
        } else {
          this.nodes.download.removeAttribute("href");
        }
      }

      if (this.nodes.external) {
        const externalUrl = validateExternalUrl(track?.externalUrl);
        this.nodes.external.hidden = !externalUrl;

        if (externalUrl) {
          this.nodes.external.href = externalUrl;
          this.nodes.external.setAttribute(
            "aria-label",
            `Open purchase or artist page for ${track.title}`
          );
          hasVisibleAction = true;
        } else {
          this.nodes.external.removeAttribute("href");
        }
      }

      setHidden(this.nodes.trackActions, !hasVisibleAction);
    }

    renderMiniPlayer(snapshot, title, artist) {
      if (!this.nodes.miniPosition) return;

      const show = Boolean(
        this.config.settings.stickyEnabled && snapshot.hasSelected && snapshot.track
      );
      this.nodes.miniPosition.hidden = !show;
      setText(this.nodes.miniTitle, title);
      setText(this.nodes.miniArtist, artist);
    }

    updateArtwork(track) {
      const artwork = validateArtworkUrl(track?.artwork);

      if (artwork && this.nodes.artwork) {
        if (this.nodes.artwork.getAttribute("src") !== artwork) {
          this.nodes.artwork.src = artwork;
        }
        this.nodes.artwork.alt =
          track?.artworkAlt || `Cover artwork for ${track?.title || "track"}`;
        this.nodes.artwork.hidden = false;
        if (this.nodes.artworkPlaceholder) {
          this.nodes.artworkPlaceholder.hidden = true;
        }
      } else {
        this.showArtworkFallback(false);
      }

      if (artwork && this.nodes.miniArtwork) {
        if (this.nodes.miniArtwork.getAttribute("src") !== artwork) {
          this.nodes.miniArtwork.src = artwork;
        }
        this.nodes.miniArtwork.hidden = false;
        if (this.nodes.miniArtworkPlaceholder) {
          this.nodes.miniArtworkPlaceholder.hidden = true;
        }
      } else {
        this.showArtworkFallback(true);
      }
    }

    showArtworkFallback(isMini) {
      const image = isMini ? this.nodes.miniArtwork : this.nodes.artwork;
      const placeholder = isMini
        ? this.nodes.miniArtworkPlaceholder
        : this.nodes.artworkPlaceholder;

      if (image) image.hidden = true;
      if (placeholder) placeholder.hidden = false;
    }

    setStatus(message) {
      const normalized = message || "";
      if (!this.nodes.status || normalized === this.lastStatusText) return;

      this.lastStatusText = normalized;
      this.nodes.status.textContent = normalized;
    }

    setError(message) {
      const normalized = message || "";
      if (!this.nodes.error || normalized === this.lastErrorText) return;

      this.lastErrorText = normalized;
      this.nodes.error.textContent = normalized;
      this.nodes.error.hidden = !normalized;
    }

    destroy() {
      this.abortController.abort();

      const miniPosition = this.nodes.miniPosition;
      if (miniPosition) {
        miniPosition.hidden = true;

        if (this.miniAnchor?.isConnected && this.miniAnchor.parentNode) {
          this.miniAnchor.parentNode.insertBefore(
            miniPosition,
            this.miniAnchor.nextSibling
          );
        } else {
          miniPosition.remove();
        }
      }

      this.miniAnchor?.remove();
      this.miniAnchor = null;
      this.isMiniPortaled = false;
    }
  }

  class PersistentAudioEngine {
    constructor(audio, source) {
      this.audio = audio;
      this.source = source;
      this.view = null;
      this.config = null;
      this.tracks = [];
      this.sectionId = "";
      this.storageKey = "";
      this.abortController = new AbortController();
      this.mediaSession = "mediaSession" in navigator ? navigator.mediaSession : null;
      this.lastSavedAt = 0;
      this.lastMediaPositionAt = 0;
      this.persistTimer = null;
      this.positionStateUnavailable = false;
      this.ignoreNextPausePersistence = false;
      this.initializedCatalog = false;
      this.destroyed = false;
      this.state = {
        index: -1,
        currentSource: "",
        currentMime: "",
        pendingSeek: null,
        pendingSeekAttempts: 0,
        loading: false,
        wantsPlayback: false,
        hasUserStartedPlayback: false,
        hasSelected: false,
        isLive: false,
        shuffle: false,
        repeat: "off",
        playlistOpen: false,
        shuffleBag: [],
        shuffleBagInitialized: false,
        shuffleHistory: [],
        status: "",
        error: "",
        sourceGeneration: 0,
        playAttempt: 0
      };

      this.audio.loop = false;
      this.audio.preload = "metadata";
      this.audio.setAttribute("playsinline", "");
      this.moveAudioToPersistentHost();
      this.bindAudioEvents();
      this.installMediaSessionHandlers();
    }

    moveAudioToPersistentHost() {
      let host = document.querySelector("[data-trnd-global-audio-host]");

      if (!host) {
        host = document.createElement("div");
        host.setAttribute("data-trnd-global-audio-host", "");
        document.body.appendChild(host);
      }

      host.appendChild(this.audio);
      this.host = host;
      this.removeDuplicateAudioElements();
    }

    adoptCandidate(candidate) {
      if (candidate && candidate !== this.audio) candidate.remove();
      // A same-document navigation may replace the body and detach the global
      // host. Reattach the original audio node; never substitute a new one.
      this.moveAudioToPersistentHost();
    }

    removeDuplicateAudioElements() {
      const candidates = document.querySelectorAll('audio[id="trnd-audio"]');
      for (const candidate of candidates) {
        if (candidate !== this.audio) candidate.remove();
      }
    }

    attachView(view) {
      const oldSectionId = this.sectionId;
      const sameSection = this.initializedCatalog && oldSectionId === view.sectionId;
      const oldTrack = this.currentTrack();
      const oldSource = this.state.currentSource;
      const oldMime = this.state.currentMime;
      const wasPlaying = this.isPlaying();

      this.view = view;
      this.config = view.config;
      this.tracks = view.config.tracks;
      this.sectionId = view.sectionId;
      this.storageKey = this.makeStorageKey(view.config);

      if (!this.initializedCatalog) {
        this.initializeCatalogFromStorage();
        this.initializedCatalog = true;
        return;
      }

      this.state.shuffleBag = [];
      this.state.shuffleHistory = [];
      this.state.shuffleBagInitialized = false;

      let matchingIndex = -1;
      if (oldTrack) {
        matchingIndex = this.tracks.findIndex((track) => track.id === oldTrack.id);
      }

      if (matchingIndex < 0 && oldSource) {
        matchingIndex = this.tracks.findIndex((track) => {
          const sourceInfo = this.describeSource(track, { quiet: true });
          return sourceInfo.ok && sourceInfo.url === oldSource;
        });
      }

      if (matchingIndex >= 0) {
        const matchingTrack = this.tracks[matchingIndex];
        const sourceInfo = this.describeSource(matchingTrack, { quiet: true });
        this.state.index = matchingIndex;

        if (
          sourceInfo.ok &&
          sourceInfo.url === oldSource &&
          sourceInfo.mime === oldMime
        ) {
          this.state.currentMime = sourceInfo.mime;
          this.state.error = "";
          if (this.state.shuffle) this.rebuildShuffleBag();
          this.updateMediaMetadata();
          this.emit();
          this.persistNow();
          return;
        }

        this.selectTrack(matchingIndex, {
          playbackIntent: wasPlaying ? "play" : "paused",
          userSelected: this.state.hasSelected,
          forceReload: true,
          origin: sameSection ? "section-update" : "view-change"
        });
        return;
      }

      this.state.hasSelected = false;
      this.state.hasUserStartedPlayback = false;
      this.state.shuffleHistory = [];
      this.state.shuffleBag = [];
      this.state.shuffleBagInitialized = false;

      if (this.tracks.length > 0) {
        this.selectTrack(0, {
          playbackIntent: "paused",
          userSelected: false,
          origin: "view-change"
        });
      } else {
        this.clearSource();
        this.state.index = -1;
        this.state.status = "Add a playlist track to begin.";
        this.emit();
      }
    }

    detachView(view) {
      if (this.view === view) this.view = null;
      this.persistNow();
    }

    initializeCatalogFromStorage() {
      const persisted = this.readPersistedState();
      const defaultRepeat = this.config.settings.loopPlaylist ? "all" : "off";

      this.state.repeat = VALID_REPEAT_MODES.has(persisted?.repeat)
        ? persisted.repeat
        : defaultRepeat;
      this.state.shuffle =
        typeof persisted?.shuffle === "boolean"
          ? persisted.shuffle
          : Boolean(this.config.settings.shuffleDefault);
      this.state.playlistOpen =
        Boolean(this.config.settings.showPlaylist) &&
        Boolean(persisted?.playlistOpen);
      this.state.hasSelected = Boolean(
        persisted &&
          (Number.isInteger(persisted.selectedTrackIndex) || persisted.selectedTrackId)
      );

      const volume = isFiniteNumber(persisted?.volume)
        ? clamp(persisted.volume, 0, 1)
        : 1;
      const muted = typeof persisted?.muted === "boolean" ? persisted.muted : false;

      try {
        this.audio.volume = volume;
        this.audio.muted = muted;
      } catch (error) {
        this.logTechnical("Volume restoration was unavailable.", {
          errorName: error?.name || "Error"
        });
      }

      if (this.tracks.length === 0) {
        this.state.index = -1;
        this.state.status = "Add a playlist track to begin.";
        this.emit();
        return;
      }

      let index = 0;
      if (persisted?.selectedTrackId) {
        const matchingIndex = this.tracks.findIndex(
          (track) => track.id === persisted.selectedTrackId
        );
        if (matchingIndex >= 0) index = matchingIndex;
      } else if (Number.isInteger(persisted?.selectedTrackIndex)) {
        index = clamp(persisted.selectedTrackIndex, 0, this.tracks.length - 1);
      }

      const restoredPosition = isFiniteNumber(persisted?.position)
        ? Math.max(0, persisted.position)
        : 0;

      this.selectTrack(index, {
        playbackIntent: "paused",
        userSelected: this.state.hasSelected,
        restorePosition: restoredPosition,
        origin: "restore"
      });
    }

    selectTrack(index, options = {}) {
      if (!Number.isInteger(index) || index < 0 || index >= this.tracks.length) {
        this.setError("That track is not available.");
        return;
      }

      const track = this.tracks[index];
      const sameTrack = index === this.state.index && Boolean(this.state.currentSource);
      const playbackIntent = options.playbackIntent || "paused";

      if (sameTrack && !options.forceReload) {
        if (options.userSelected) this.state.hasSelected = true;
        this.state.error = "";
        this.state.status = `Selected ${track.title} by ${track.artist}.`;

        if (playbackIntent === "play") {
          this.requestPlay({ origin: options.origin || "same-track" });
        } else {
          this.emit();
          this.persistNow();
        }
        return;
      }

      const wasPlaying = this.isPlaying();
      this.persistNow();

      if (!this.audio.paused) {
        this.ignoreNextPausePersistence = true;
        this.audio.pause();
      }

      this.state.sourceGeneration += 1;
      this.state.playAttempt += 1;
      this.state.wantsPlayback = false;
      this.state.loading = true;
      this.state.error = "";
      this.state.isLive = false;
      this.state.pendingSeek = isFiniteNumber(options.restorePosition)
        ? Math.max(0, options.restorePosition)
        : 0;
      this.state.pendingSeekAttempts = 0;

      if (
        this.state.index >= 0 &&
        this.state.index !== index &&
        options.recordHistory !== false
      ) {
        this.state.shuffleHistory.push(this.state.index);
        if (this.state.shuffleHistory.length > 100) this.state.shuffleHistory.shift();
      }

      this.state.index = index;
      if (options.userSelected) this.state.hasSelected = true;
      if (this.state.shuffle && !this.state.shuffleBagInitialized) {
        this.rebuildShuffleBag();
      } else {
        this.removeIndexFromShuffleBag(index);
      }

      const sourceInfo = this.describeSource(track);
      this.state.currentSource = sourceInfo.ok ? sourceInfo.url : "";
      this.state.currentMime = sourceInfo.ok ? sourceInfo.mime : "";
      this.state.status = `Selected ${track.title} by ${track.artist}.`;

      this.updateMediaMetadata();
      this.emit();

      if (!sourceInfo.ok) {
        this.clearSource();
        this.state.loading = false;
        this.setError(sourceInfo.message);
        this.persistNow(0);
        return;
      }

      this.assignSource(sourceInfo.url, sourceInfo.mime);
      this.persistNow(this.state.pendingSeek || 0);

      const shouldPlay =
        playbackIntent === "play" ||
        (playbackIntent === "continue" && wasPlaying);

      if (shouldPlay) {
        this.requestPlay({ origin: options.origin || "track-change" });
      }
    }

    assignSource(url, mime) {
      this.source.setAttribute("src", url);
      if (mime) this.source.setAttribute("type", mime);
      else this.source.removeAttribute("type");

      this.audio.setAttribute("src", url);
      this.audio.load();
    }

    clearSource() {
      this.state.sourceGeneration += 1;
      this.state.playAttempt += 1;
      this.state.currentSource = "";
      this.state.currentMime = "";
      this.state.pendingSeek = null;
      this.state.loading = false;
      this.state.wantsPlayback = false;
      this.audio.removeAttribute("src");
      this.source.setAttribute("src", "");
      this.source.setAttribute("type", "audio/mpeg");
      this.audio.load();
    }

    togglePlay(options = {}) {
      if (!this.currentTrack()) {
        this.setError("Add a track before pressing Play.");
        return;
      }

      if (this.isPlaying() || this.state.wantsPlayback) {
        this.state.wantsPlayback = false;
        this.state.loading = false;
        this.audio.pause();
        this.state.status = "Playback paused.";
        this.emit();
        this.persistNow();
        return;
      }

      if (this.state.error || !this.state.currentSource) {
        this.selectTrack(this.state.index, {
          playbackIntent: "play",
          userSelected: true,
          forceReload: true,
          origin: options.origin || "play-retry"
        });
        return;
      }

      if (this.audio.ended && this.canSeek()) {
        this.safeSetCurrentTime(0);
      }

      this.state.hasSelected = true;
      this.requestPlay({ origin: options.origin || "play" });
    }

    async requestPlay(options = {}) {
      if (!this.state.currentSource) {
        this.setError("This track does not have a valid direct audio URL.");
        return;
      }

      const attempt = ++this.state.playAttempt;
      const sourceGeneration = this.state.sourceGeneration;
      this.state.wantsPlayback = true;
      this.state.loading = true;
      this.state.error = "";
      this.state.status = "Starting playback…";
      this.emit();

      try {
        const playResult = this.audio.play();
        if (playResult && typeof playResult.then === "function") {
          await playResult;
        }

        if (
          attempt !== this.state.playAttempt ||
          sourceGeneration !== this.state.sourceGeneration
        ) {
          return;
        }

        this.state.hasUserStartedPlayback = true;
        this.state.hasSelected = true;
      } catch (error) {
        if (
          attempt !== this.state.playAttempt ||
          sourceGeneration !== this.state.sourceGeneration ||
          error?.name === "AbortError"
        ) {
          return;
        }

        this.state.wantsPlayback = false;
        this.state.loading = false;

        if (error?.name === "NotAllowedError") {
          this.state.status = "Tap Play to start audio.";
          this.state.error = "";
        } else if (error?.name === "NotSupportedError") {
          this.state.error = FRIENDLY_MEDIA_ERROR;
          this.state.status = "";
        } else {
          this.state.error = FRIENDLY_MEDIA_ERROR;
          this.state.status = "";
        }

        this.logTechnical("Playback request failed.", {
          errorName: error?.name || "Error",
          origin: options.origin || "unknown",
          trackId: this.currentTrack()?.id || "none",
          mime: this.state.currentMime || "unknown",
          url: redactUrl(this.state.currentSource)
        });
        this.syncMediaPlaybackState("paused");
        this.emit();
      }
    }

    moveTrack(direction, options = {}) {
      if (this.tracks.length === 0) return;

      const wasPlaying = this.isPlaying() || this.state.wantsPlayback;
      let nextIndex;
      let recordHistory = true;

      if (this.state.shuffle) {
        if (direction < 0 && this.state.shuffleHistory.length > 0) {
          nextIndex = this.state.shuffleHistory.pop();
          recordHistory = false;
        } else {
          nextIndex = this.takeShuffleIndex({ allowRefill: true });
        }
      } else {
        const base = this.state.index >= 0 ? this.state.index : 0;
        nextIndex = (base + direction + this.tracks.length) % this.tracks.length;
      }

      if (!Number.isInteger(nextIndex)) return;

      if (nextIndex === this.state.index) {
        if (this.canSeek()) this.safeSetCurrentTime(0);
        if (wasPlaying) this.requestPlay({ origin: options.origin || "track-control" });
        else {
          this.state.status = `Selected ${this.currentTrack()?.title || "track"}.`;
          this.emit();
          this.persistNow(0);
        }
        return;
      }

      this.selectTrack(nextIndex, {
        playbackIntent: wasPlaying ? "play" : "paused",
        userSelected: true,
        recordHistory,
        origin: options.origin || "track-control"
      });
    }

    handleEnded() {
      this.state.loading = false;
      this.state.wantsPlayback = false;
      this.syncMediaPlaybackState("none");
      this.persistNow(0);

      if (!this.config?.settings.autoplayNext) {
        this.state.status = "Track ended.";
        this.emit();
        return;
      }

      if (this.state.repeat === "one") {
        if (this.safeSetCurrentTime(0)) {
          this.requestPlay({ origin: "repeat-one" });
        }
        return;
      }

      let nextIndex = null;

      if (this.state.shuffle) {
        nextIndex = this.takeShuffleIndex({
          allowRefill: this.state.repeat === "all"
        });
      } else if (this.state.index < this.tracks.length - 1) {
        nextIndex = this.state.index + 1;
      } else if (this.state.repeat === "all") {
        nextIndex = 0;
      }

      if (!Number.isInteger(nextIndex)) {
        this.state.status = "Playlist ended.";
        this.emit();
        return;
      }

      this.selectTrack(nextIndex, {
        playbackIntent: "play",
        userSelected: true,
        origin: "autoplay-next"
      });
    }

    toggleShuffle() {
      this.state.shuffle = !this.state.shuffle;
      this.state.shuffleHistory = [];
      this.state.shuffleBagInitialized = false;
      this.rebuildShuffleBag();
      this.state.status = `Shuffle ${this.state.shuffle ? "on" : "off"}.`;
      this.emit();
      this.persistNow();
    }

    cycleRepeat() {
      const order = ["off", "all", "one"];
      const currentIndex = order.indexOf(this.state.repeat);
      this.state.repeat = order[(currentIndex + 1) % order.length];
      this.state.status = `Repeat ${this.state.repeat}.`;
      this.emit();
      this.persistNow();
    }

    rebuildShuffleBag() {
      if (!this.state.shuffle || this.tracks.length < 2) {
        this.state.shuffleBag = [];
        this.state.shuffleBagInitialized = false;
        return;
      }

      const indices = [];
      for (let index = 0; index < this.tracks.length; index += 1) {
        if (index !== this.state.index) indices.push(index);
      }

      for (let index = indices.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [indices[index], indices[swapIndex]] = [indices[swapIndex], indices[index]];
      }

      this.state.shuffleBag = indices;
      this.state.shuffleBagInitialized = true;
    }

    takeShuffleIndex({ allowRefill }) {
      if (this.tracks.length < 2) return this.state.index;

      if (this.state.shuffleBag.length === 0 && allowRefill) {
        this.rebuildShuffleBag();
      }

      return this.state.shuffleBag.length > 0
        ? this.state.shuffleBag.pop()
        : null;
    }

    removeIndexFromShuffleBag(index) {
      this.state.shuffleBag = this.state.shuffleBag.filter(
        (candidate) => candidate !== index
      );
    }

    toggleMute() {
      this.audio.muted = !this.audio.muted;
      this.state.status = this.audio.muted ? "Muted." : "Unmuted.";
      this.emit();
      this.persistNow();
    }

    setVolume(value) {
      if (!isFiniteNumber(value)) return;

      try {
        this.audio.volume = clamp(value, 0, 1);
        if (value > 0 && this.audio.muted) this.audio.muted = false;
      } catch (error) {
        this.logTechnical("Volume adjustment was unavailable.", {
          errorName: error?.name || "Error"
        });
      }
    }

    adjustVolume(delta) {
      const nextVolume = clamp(this.audio.volume + delta, 0, 1);
      this.setVolume(nextVolume);
      this.state.status = `Volume ${Math.round(nextVolume * 100)} percent.`;
      this.emit();
      this.persistNow();
    }

    togglePlaylist() {
      this.setPlaylistOpen(!this.state.playlistOpen, {
        persist: true,
        announce: true
      });
    }

    setPlaylistOpen(open, options = {}) {
      if (!this.config?.settings.showPlaylist) return;

      this.state.playlistOpen = Boolean(open);
      if (options.announce !== false) {
        this.state.status = `Playlist ${open ? "opened" : "closed"}.`;
      }
      this.emit();
      if (options.persist !== false) this.persistNow();
    }

    seekBy(delta, options = {}) {
      if (!this.canSeek() || !isFiniteNumber(delta)) {
        if (this.state.isLive) {
          this.state.status = "Seeking is unavailable for this live stream.";
          this.emit();
        }
        return;
      }

      this.seekTo(this.audio.currentTime + delta, options);
    }

    seekTo(seconds, options = {}) {
      if (!this.canSeek() || !isFiniteNumber(seconds)) return;

      const duration = this.getFiniteDuration();
      const target = clamp(seconds, 0, duration);

      try {
        if (options.fastSeek && typeof this.audio.fastSeek === "function") {
          this.audio.fastSeek(target);
        } else {
          this.audio.currentTime = target;
        }
        this.state.pendingSeek = null;
        this.state.status = `Position ${formatSpokenTime(target)}.`;
        this.updateMediaPositionState(true);
        this.emit();
        this.persistNow(target);
      } catch (error) {
        this.logTechnical("Seek was unavailable.", {
          errorName: error?.name || "Error",
          origin: options.origin || "unknown",
          trackId: this.currentTrack()?.id || "none"
        });
      }
    }

    safeSetCurrentTime(seconds) {
      if (!this.canSeek() || !isFiniteNumber(seconds)) return false;

      try {
        this.audio.currentTime = clamp(seconds, 0, this.getFiniteDuration());
        return true;
      } catch (error) {
        this.logTechnical("Playback position could not be changed.", {
          errorName: error?.name || "Error"
        });
        return false;
      }
    }

    restorePendingSeek() {
      if (!isFiniteNumber(this.state.pendingSeek) || !this.canSeek()) return;

      const duration = this.getFiniteDuration();
      let target = clamp(this.state.pendingSeek, 0, duration);
      if (target >= duration - 1) target = 0;

      try {
        this.audio.currentTime = target;
        this.state.pendingSeek = null;
        this.state.pendingSeekAttempts = 0;
      } catch (error) {
        this.state.pendingSeekAttempts += 1;
        if (this.state.pendingSeekAttempts > 1) {
          this.state.pendingSeek = null;
          this.logTechnical("Saved position could not be restored.", {
            errorName: error?.name || "Error",
            trackId: this.currentTrack()?.id || "none"
          });
        }
      }
    }

    describeSource(track, options = {}) {
      const raw = String(track?.src || "").trim();

      if (!raw) {
        return {
          ok: false,
          message: "This track does not have an audio URL. Add a direct file or stream URL."
        };
      }

      let parsed;
      try {
        parsed = new URL(raw, window.location.href);
      } catch (error) {
        return { ok: false, message: FRIENDLY_MEDIA_ERROR };
      }

      if (!new Set(["http:", "https:"]).has(parsed.protocol)) {
        return {
          ok: false,
          message: "The audio URL must use HTTP or HTTPS."
        };
      }

      if (parsed.username || parsed.password) {
        return {
          ok: false,
          message: "Authenticated media URLs are not supported."
        };
      }

      if (
        window.location.protocol === "https:" &&
        parsed.protocol !== "https:"
      ) {
        return {
          ok: false,
          message: "Use an HTTPS audio URL on this secure storefront."
        };
      }

      if (BLOCKED_AUDIO_HOSTS.some((pattern) => pattern.test(parsed.hostname))) {
        return {
          ok: false,
          message:
            "Use a direct audio file or stream URL, not a YouTube, Spotify, or SoundCloud webpage."
        };
      }

      const override = String(track?.mime || "auto").trim();
      const mime = VALID_MIME_TYPES.has(override)
        ? override
        : inferAudioMime(parsed.pathname);
      const looksLikeHls =
        HLS_MIME_TYPES.has(mime) || parsed.pathname.toLowerCase().endsWith(".m3u8");

      if (looksLikeHls && !this.supportsNativeHls()) {
        if (!options.quiet) {
          this.logTechnical("Native HLS playback is unavailable in this browser.", {
            trackId: track?.id || "none",
            mime: mime || "unknown",
            url: redactUrl(parsed.href)
          });
        }

        return {
          ok: false,
          message:
            "This HLS stream is not supported by this browser. Try Safari or provide an MP3, AAC, or M4A file."
        };
      }

      if (mime && !looksLikeHls && !this.audio.canPlayType(mime) && !options.quiet) {
        this.logTechnical("The browser did not report support for this MIME type; native loading will still be attempted.", {
          trackId: track?.id || "none",
          mime,
          url: redactUrl(parsed.href)
        });
      }

      return { ok: true, url: parsed.href, mime };
    }

    supportsNativeHls() {
      return Boolean(
        this.audio.canPlayType("application/vnd.apple.mpegurl") ||
          this.audio.canPlayType("audio/x-mpegurl")
      );
    }

    bindAudioEvents() {
      const signal = this.abortController.signal;

      this.audio.addEventListener(
        "loadstart",
        () => {
          if (!this.state.currentSource) return;
          this.state.loading = true;
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "loadedmetadata",
        () => {
          this.updateTimelineKind();
          this.restorePendingSeek();
          if (!this.state.wantsPlayback) this.state.loading = false;
          this.updateMediaPositionState(true);
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "durationchange",
        () => {
          this.updateTimelineKind();
          this.restorePendingSeek();
          this.updateMediaPositionState(true);
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "canplay",
        () => {
          this.updateTimelineKind();
          this.restorePendingSeek();
          if (!this.state.wantsPlayback) this.state.loading = false;
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "play",
        () => {
          this.state.wantsPlayback = true;
          this.syncMediaPlaybackState("playing");
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "playing",
        () => {
          this.state.loading = false;
          this.state.wantsPlayback = false;
          this.state.error = "";
          this.state.hasUserStartedPlayback = true;
          this.state.hasSelected = true;
          this.state.status = `Playing ${this.currentTrack()?.title || "track"}.`;
          this.updateTimelineKind();
          this.syncMediaPlaybackState("playing");
          this.updateMediaPositionState(true);
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "pause",
        () => {
          this.state.wantsPlayback = false;
          if (!this.audio.ended) this.state.loading = false;
          this.syncMediaPlaybackState(this.audio.ended ? "none" : "paused");
          this.emit();

          if (this.ignoreNextPausePersistence) {
            this.ignoreNextPausePersistence = false;
          } else {
            this.persistNow();
          }
        },
        { signal }
      );

      this.audio.addEventListener(
        "waiting",
        () => {
          if (this.audio.paused) return;
          this.state.loading = true;
          this.state.status = "Buffering…";
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "stalled",
        () => {
          if (!this.state.currentSource) return;
          this.state.status = "Connection interrupted. The player is retrying…";
          this.emit();
          this.logTechnical("Media loading stalled; the native player will retry.", {
            trackId: this.currentTrack()?.id || "none",
            mime: this.state.currentMime || "unknown",
            url: redactUrl(this.state.currentSource)
          });
        },
        { signal }
      );

      this.audio.addEventListener(
        "seeking",
        () => {
          if (!this.audio.paused) this.state.loading = true;
          this.emit();
        },
        { signal }
      );

      this.audio.addEventListener(
        "seeked",
        () => {
          this.state.loading = false;
          this.updateMediaPositionState(true);
          this.emit();
          this.persistNow();
        },
        { signal }
      );

      this.audio.addEventListener(
        "timeupdate",
        () => {
          this.updateTimelineKind();
          this.updateMediaPositionState(false);
          this.emit();

          const now = Date.now();
          if (now - this.lastSavedAt >= 5000) this.persistNow();
        },
        { signal }
      );

      this.audio.addEventListener(
        "volumechange",
        () => {
          this.emit();
          this.persistSoon();
        },
        { signal }
      );

      this.audio.addEventListener(
        "ended",
        () => this.handleEnded(),
        { signal }
      );

      this.audio.addEventListener(
        "error",
        () => this.handleMediaError(),
        { signal }
      );

      window.addEventListener(
        "pagehide",
        () => this.persistNow(),
        { signal, passive: true }
      );
    }

    handleMediaError() {
      if (!this.state.currentSource) return;

      const mediaError = this.audio.error;
      const code = mediaError?.code || 0;
      const codeName =
        {
          1: "MEDIA_ERR_ABORTED",
          2: "MEDIA_ERR_NETWORK",
          3: "MEDIA_ERR_DECODE",
          4: "MEDIA_ERR_SRC_NOT_SUPPORTED"
        }[code] || "MEDIA_ERR_UNKNOWN";

      this.state.loading = false;
      this.state.wantsPlayback = false;
      this.state.error = FRIENDLY_MEDIA_ERROR;
      this.state.status = "";
      this.syncMediaPlaybackState("none");
      this.emit();

      this.logTechnical("Native media error.", {
        code,
        codeName,
        trackId: this.currentTrack()?.id || "none",
        mime: this.state.currentMime || "unknown",
        url: redactUrl(this.state.currentSource)
      });
    }

    updateTimelineKind() {
      const duration = this.audio.duration;
      const hasMetadata = this.audio.readyState >= HTMLMediaElement.HAVE_METADATA;
      const activelyPlaying = !this.audio.paused && Boolean(this.state.currentSource);

      this.state.isLive =
        Boolean(this.state.currentSource) &&
        !isFinitePositive(duration) &&
        (hasMetadata || activelyPlaying);
    }

    canSeek() {
      return Boolean(this.state.currentSource && isFinitePositive(this.audio.duration));
    }

    getFiniteDuration() {
      return this.canSeek() ? this.audio.duration : null;
    }

    isPlaying() {
      return !this.audio.paused && !this.audio.ended;
    }

    currentTrack() {
      return this.state.index >= 0 ? this.tracks[this.state.index] || null : null;
    }

    snapshot() {
      const duration = this.getFiniteDuration();
      const currentTime = isFiniteNumber(this.audio.currentTime)
        ? Math.max(0, this.audio.currentTime)
        : 0;

      return {
        index: this.state.index,
        track: this.currentTrack(),
        trackCount: this.tracks.length,
        isPlaying: this.isPlaying(),
        loading: this.state.loading,
        hasUserStartedPlayback: this.state.hasUserStartedPlayback,
        hasSelected: this.state.hasSelected,
        isLive: this.state.isLive,
        hasFiniteDuration: isFinitePositive(duration),
        canSeek: this.canSeek(),
        duration: duration || 0,
        currentTime,
        volume: clamp(this.audio.volume, 0, 1),
        muted: this.audio.muted,
        shuffle: this.state.shuffle,
        repeat: this.state.repeat,
        playlistOpen: this.state.playlistOpen,
        status: this.state.status,
        error: this.state.error,
        validatedSource: this.state.currentSource
      };
    }

    emit() {
      if (this.destroyed) return;
      this.view?.render(this.snapshot());
    }

    setError(message) {
      this.state.loading = false;
      this.state.wantsPlayback = false;
      this.state.error = message || FRIENDLY_MEDIA_ERROR;
      this.state.status = "";
      this.emit();
    }

    makeStorageKey(config) {
      const editorSuffix = config.designMode ? ":editor" : "";
      return `${STORAGE_PREFIX}${config.sectionId}${editorSuffix}`;
    }

    readPersistedState() {
      if (!this.storageKey) return null;

      try {
        const raw = window.localStorage.getItem(this.storageKey);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch (error) {
        this.logTechnical("Saved player state could not be read.", {
          errorName: error?.name || "Error"
        });
        return null;
      }
    }

    persistNow(positionOverride) {
      if (!this.storageKey || this.state.index < 0) return;

      if (this.persistTimer) {
        window.clearTimeout(this.persistTimer);
        this.persistTimer = null;
      }

      const position = isFiniteNumber(positionOverride)
        ? Math.max(0, positionOverride)
        : this.canSeek() && isFiniteNumber(this.audio.currentTime)
          ? Math.max(0, this.audio.currentTime)
          : 0;
      const payload = {
        selectedTrackIndex: this.state.index,
        selectedTrackId: this.currentTrack()?.id || "",
        position,
        volume: clamp(this.audio.volume, 0, 1),
        muted: Boolean(this.audio.muted),
        shuffle: Boolean(this.state.shuffle),
        repeat: VALID_REPEAT_MODES.has(this.state.repeat)
          ? this.state.repeat
          : "off",
        playlistOpen: Boolean(this.state.playlistOpen)
      };

      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
        this.lastSavedAt = Date.now();
      } catch (error) {
        this.logTechnical("Player state could not be saved.", {
          errorName: error?.name || "Error"
        });
      }
    }

    persistSoon(delay = 350) {
      if (this.persistTimer) window.clearTimeout(this.persistTimer);
      this.persistTimer = window.setTimeout(() => {
        this.persistTimer = null;
        this.persistNow();
      }, delay);
    }

    installMediaSessionHandlers() {
      if (!this.mediaSession?.setActionHandler) return;

      const handlers = {
        play: () => this.requestPlay({ origin: "media-session" }),
        pause: () => {
          this.state.wantsPlayback = false;
          this.audio.pause();
        },
        previoustrack: () => this.moveTrack(-1, { origin: "media-session" }),
        nexttrack: () => this.moveTrack(1, { origin: "media-session" }),
        seekbackward: (details = {}) =>
          this.seekBy(-(isFiniteNumber(details.seekOffset) ? details.seekOffset : 10), {
            origin: "media-session"
          }),
        seekforward: (details = {}) =>
          this.seekBy(isFiniteNumber(details.seekOffset) ? details.seekOffset : 10, {
            origin: "media-session"
          }),
        seekto: (details = {}) => {
          if (!isFiniteNumber(details.seekTime)) return;
          this.seekTo(details.seekTime, {
            origin: "media-session",
            fastSeek: Boolean(details.fastSeek)
          });
        },
        stop: () => {
          this.state.wantsPlayback = false;
          this.audio.pause();
          if (this.canSeek()) this.safeSetCurrentTime(0);
          this.state.status = "Playback stopped.";
          this.syncMediaPlaybackState("none");
          this.emit();
          this.persistNow(0);
        }
      };

      for (const action of MEDIA_ACTIONS) {
        try {
          this.mediaSession.setActionHandler(action, handlers[action]);
        } catch (error) {
          this.logTechnical("Media Session action is unavailable.", {
            action,
            errorName: error?.name || "Error"
          });
        }
      }
    }

    updateMediaMetadata() {
      if (!this.mediaSession || !("MediaMetadata" in window)) return;

      const track = this.currentTrack();
      if (!track) return;

      const artworkUrl = validateArtworkUrl(track.artwork);
      const mediaArtworkUrl = validateArtworkUrl(track.mediaArtwork) || artworkUrl;
      const metadata = {
        title: track.title,
        artist: track.artist,
        album: track.album || ""
      };

      if (mediaArtworkUrl) {
        metadata.artwork = [
          {
            src: mediaArtworkUrl,
            sizes: "512x512",
            type: inferImageMime(mediaArtworkUrl)
          }
        ];
      }

      try {
        this.mediaSession.metadata = new window.MediaMetadata(metadata);
      } catch (error) {
        this.logTechnical("Media Session metadata could not be updated.", {
          errorName: error?.name || "Error",
          trackId: track.id
        });
      }
    }

    syncMediaPlaybackState(state) {
      if (!this.mediaSession || !("playbackState" in this.mediaSession)) return;

      try {
        this.mediaSession.playbackState = state;
      } catch (error) {
        this.logTechnical("Media Session playback state was unavailable.", {
          errorName: error?.name || "Error"
        });
      }
    }

    updateMediaPositionState(force) {
      if (
        this.positionStateUnavailable ||
        !this.mediaSession?.setPositionState ||
        !this.canSeek()
      ) {
        return;
      }

      const now = Date.now();
      if (!force && now - this.lastMediaPositionAt < 1000) return;

      const duration = this.audio.duration;
      const currentTime = this.audio.currentTime;
      const playbackRate = this.audio.playbackRate;

      if (
        !isFinitePositive(duration) ||
        !isFiniteNumber(currentTime) ||
        currentTime < 0 ||
        !isFinitePositive(playbackRate)
      ) {
        return;
      }

      try {
        this.mediaSession.setPositionState({
          duration,
          position: clamp(currentTime, 0, duration),
          playbackRate
        });
        this.lastMediaPositionAt = now;
      } catch (error) {
        this.positionStateUnavailable = true;
        this.logTechnical("Media Session position state was unavailable.", {
          errorName: error?.name || "Error"
        });
      }
    }

    logTechnical(message, details = {}) {
      console.warn(`[TRND PortalKey] ${message}`, details);
    }

    destroy({ removeAudio = true } = {}) {
      if (this.destroyed) return;

      this.persistNow();
      this.destroyed = true;
      if (this.persistTimer) window.clearTimeout(this.persistTimer);
      this.abortController.abort();

      if (this.mediaSession?.setActionHandler) {
        for (const action of MEDIA_ACTIONS) {
          try {
            this.mediaSession.setActionHandler(action, null);
          } catch (error) {
            // Some browsers expose Media Session but not every action.
          }
        }
      }

      if (removeAudio) {
        this.audio.pause();
        this.audio.removeAttribute("src");
        this.source.setAttribute("src", "");
        this.audio.load();
        this.audio.remove();
        if (this.host && this.host.childElementCount === 0) this.host.remove();
      }
    }
  }

  function parseConfig(root) {
    const node = root.querySelector("[data-trnd-config]");
    if (!node) throw new Error("Player configuration is missing.");

    const raw = JSON.parse(node.textContent || "{}");
    const tracks = Array.isArray(raw.tracks)
      ? raw.tracks.map((track, index) => sanitizeTrack(track, index))
      : [];

    return {
      sectionId: String(raw.sectionId || root.dataset.sectionId || "default"),
      designMode: Boolean(raw.designMode),
      defaultArtwork: String(raw.defaultArtwork || ""),
      settings: {
        showPlaylist: Boolean(raw.settings?.showPlaylist),
        autoplayNext: Boolean(raw.settings?.autoplayNext),
        loopPlaylist: Boolean(raw.settings?.loopPlaylist),
        shuffleDefault: Boolean(raw.settings?.shuffleDefault),
        stickyEnabled: Boolean(raw.settings?.stickyEnabled),
        stickyPosition: String(raw.settings?.stickyPosition || "bottom-center")
      },
      tracks
    };
  }

  function sanitizeTrack(track, index) {
    return {
      id: String(track?.id || `track-${index}`),
      title: String(track?.title || "Untitled track"),
      artist: String(track?.artist || "Unknown artist"),
      album: String(track?.album || ""),
      src: String(track?.src || ""),
      mime: String(track?.mime || "auto"),
      artwork: String(track?.artwork || ""),
      mediaArtwork: String(track?.mediaArtwork || ""),
      artworkAlt: String(track?.artworkAlt || ""),
      explicit: Boolean(track?.explicit),
      description: String(track?.description || ""),
      download: Boolean(track?.download),
      externalUrl: String(track?.externalUrl || "")
    };
  }

  function findRoots(scope) {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    return Array.from(new Set(roots));
  }

  function showRootError(root, message) {
    const error = root.querySelector("[data-trnd-error]");
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }

    for (const control of root.querySelectorAll("button, input[type='range']")) {
      control.disabled = true;
    }
  }

  const api = {
    version: VERSION,
    destroyed: false,
    engine: null,
    inheritedAudio,
    views: new Map(),
    cleanupTimers: new Map(),
    lifecycleController: new AbortController(),

    scan(scope = document) {
      for (const root of findRoots(scope)) this.mount(root);
    },

    mount(root) {
      if (!(root instanceof Element)) return null;

      let config;
      try {
        config = parseConfig(root);
      } catch (error) {
        showRootError(root, "The player configuration could not be loaded.");
        console.error("[TRND PortalKey] Invalid section configuration.", {
          errorName: error?.name || "Error"
        });
        return null;
      }

      const existingView = this.views.get(config.sectionId);
      if (existingView?.root === root) return existingView;

      const renderedCandidate = root.querySelector('audio[id="trnd-audio"]');
      const candidateAudio =
        this.inheritedAudio?.tagName === "AUDIO"
          ? this.inheritedAudio
          : renderedCandidate;

      if (!this.engine) {
        if (!candidateAudio) {
          showRootError(root, "The required HTML5 audio element is missing.");
          console.error("[TRND PortalKey] The section must contain audio#trnd-audio.");
          return null;
        }

        const source = candidateAudio.querySelector("source");
        if (!source) {
          showRootError(root, "The required audio source element is missing.");
          console.error("[TRND PortalKey] The audio element must contain one source element.");
          return null;
        }

        this.engine = new PersistentAudioEngine(candidateAudio, source);
        if (renderedCandidate && renderedCandidate !== candidateAudio) {
          this.engine.adoptCandidate(renderedCandidate);
        }
        this.inheritedAudio = null;
      } else {
        this.engine.adoptCandidate(renderedCandidate);
      }

      const activeView = this.engine.view;
      if (
        activeView &&
        activeView.sectionId !== config.sectionId &&
        activeView.root.isConnected
      ) {
        showRootError(
          root,
          "Only one TRND PortalKey Player section can be active on a page. Remove the duplicate section."
        );
        console.warn(
          "[TRND PortalKey] Duplicate player section detected; the second view was disabled."
        );
        return null;
      }

      if (existingView) {
        existingView.destroy();
        this.views.delete(config.sectionId);
      }

      if (activeView && activeView !== existingView && !activeView.root.isConnected) {
        activeView.destroy();
        this.views.delete(activeView.sectionId);
      }

      const cleanupTimer = this.cleanupTimers.get(config.sectionId);
      if (cleanupTimer) {
        window.clearTimeout(cleanupTimer);
        this.cleanupTimers.delete(config.sectionId);
      }

      const view = new PortalKeyView(root, config, this.engine);
      this.views.set(config.sectionId, view);
      root.dataset.trndInitialized = VERSION;
      this.engine.attachView(view);
      return view;
    },

    unmount(root) {
      if (!(root instanceof Element)) return;

      const configId = String(root.dataset.sectionId || "");
      const view = this.views.get(configId);
      if (!view || view.root !== root) return;

      view.destroy();
      this.views.delete(configId);
      this.engine?.detachView(view);
      delete root.dataset.trndInitialized;

      const isDesignMode = Boolean(window.Shopify?.designMode);
      if (!isDesignMode) return;

      const timer = window.setTimeout(() => {
        this.cleanupTimers.delete(configId);
        const replacement = Array.from(document.querySelectorAll(ROOT_SELECTOR)).find(
          (candidate) => candidate.dataset.sectionId === configId
        );

        if (replacement) {
          this.mount(replacement);
          return;
        }

        if (this.engine?.sectionId === configId && !this.engine.view) {
          this.engine.destroy();
          this.engine = null;
        }
      }, 750);

      this.cleanupTimers.set(configId, timer);
    },

    destroy({ keepAudio = false } = {}) {
      if (this.destroyed) return;
      this.destroyed = true;
      this.lifecycleController.abort();

      for (const timer of this.cleanupTimers.values()) window.clearTimeout(timer);
      this.cleanupTimers.clear();

      for (const view of this.views.values()) view.destroy();
      this.views.clear();

      this.engine?.destroy({ removeAudio: !keepAudio });
      this.engine = null;
    }
  };

  window.TRNDPortalKeyPlayer = api;

  const lifecycleSignal = api.lifecycleController.signal;

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      api.scan(event.target);
    },
    { signal: lifecycleSignal }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      const roots = findRoots(event.target);
      for (const root of roots) api.unmount(root);
    },
    { signal: lifecycleSignal }
  );

  document.addEventListener(
    "shopify:section:select",
    (event) => {
      api.scan(event.target);
    },
    { signal: lifecycleSignal }
  );

  document.addEventListener(
    "shopify:section:deselect",
    () => {
      // Deliberately no playback change when the editor deselects the section.
    },
    { signal: lifecycleSignal }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const sectionId = String(event.detail?.sectionId || "");
      const blockId = String(event.detail?.blockId || "");
      const view = api.views.get(sectionId);
      view?.openPlaylistFromEditor(blockId);
    },
    { signal: lifecycleSignal }
  );

  api.scan(document);

  function inferAudioMime(pathname) {
    const path = String(pathname || "").toLowerCase();
    if (path.endsWith(".mp3")) return "audio/mpeg";
    if (path.endsWith(".aac")) return "audio/aac";
    if (path.endsWith(".m4a") || path.endsWith(".mp4")) return "audio/mp4";
    if (path.endsWith(".m3u8")) return "application/vnd.apple.mpegurl";
    return "";
  }

  function inferImageMime(url) {
    try {
      const path = new URL(url, window.location.href).pathname.toLowerCase();
      if (path.endsWith(".webp")) return "image/webp";
      if (path.endsWith(".gif")) return "image/gif";
      if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
    } catch (error) {
      // The URL was already validated; PNG is a safe metadata fallback.
    }
    return "image/png";
  }

  function validateArtworkUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    try {
      const url = new URL(raw, window.location.href);
      if (!new Set(["http:", "https:"]).has(url.protocol)) return "";
      if (url.username || url.password) return "";
      if (window.location.protocol === "https:" && url.protocol !== "https:") {
        return "";
      }
      return url.href;
    } catch (error) {
      return "";
    }
  }

  function validateExternalUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    try {
      const url = new URL(raw, window.location.href);
      if (!new Set(["http:", "https:"]).has(url.protocol)) return "";
      if (url.username || url.password) return "";
      if (window.location.protocol === "https:" && url.protocol !== "https:") return "";
      return url.href;
    } catch (error) {
      return "";
    }
  }

  function redactUrl(value) {
    if (!value) return "none";

    try {
      const url = new URL(value, window.location.href);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return "invalid-url";
    }
  }

  function formatClockTime(seconds) {
    if (!isFiniteNumber(seconds) || seconds < 0) return "0:00";

    const whole = Math.floor(seconds);
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const remainingSeconds = whole % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds
      ).padStart(2, "0")}`;
    }

    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  }

  function formatSpokenTime(seconds) {
    if (!isFiniteNumber(seconds) || seconds < 0) return "0 seconds";

    const whole = Math.floor(seconds);
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const remainingSeconds = whole % 60;
    const parts = [];

    if (hours) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    if (minutes) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
    if (remainingSeconds || parts.length === 0) {
      parts.push(
        `${remainingSeconds} ${remainingSeconds === 1 ? "second" : "seconds"}`
      );
    }

    return parts.join(" ");
  }

  function setText(node, value) {
    if (node) node.textContent = String(value || "");
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = Boolean(hidden);
  }

  function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
  }

  function isFinitePositive(value) {
    return isFiniteNumber(value) && value > 0;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }
})();
